<template>
  <div class="embed-editor-view bg-grey-150 dark:bg-grey-850 relative flex h-full w-full flex-col overflow-hidden px-3">
    <embed-top-bar :map-name="mapTitle" :is-toolbar-visible="isToolbarVisible" :is-menu-pop-visible="embedEditorPreset.showMenuPop">
      <template #right v-if="shouldShowPanelTab">
        <panel-tab v-model="rightPanelTabModel" :tabs="rightPanelTabs" :disabled="!isMapEditable || !isEditorLoaded" />
      </template>
    </embed-top-bar>

    <section-slider class="relative flex-1" :is-show-left-panel="false" :is-show-right-panel="isShowRightPanel">
      <template #middle>
        <Editor class="tw-section-container overflow-hidden" :cache-id="cacheId" :file="file" :file-name="fileName" :new-doc="newDoc" />
        <EmbedEditorContextMenu ref="contextMenuRef" />
        <LabelEditor
          v-if="isLabelPopoverOpened"
          :workbook-id="workbookId"
          :is-opened="isLabelPopoverOpened"
          @visibility-change="handleLabelPopoverVisibilityChange"
        />
        <NotesEditor
          v-if="isNotesPopoverOpened"
          :workbook-id="workbookId"
          :is-opened="isNotesPopoverOpened"
          @visibility-change="handleNotePopoverVisibilityChange"
        />
        <HyperlinkEditor
          v-if="isHyperlinkPopoverOpened"
          :workbook-id="workbookId"
          :is-opened="isHyperlinkPopoverOpened"
          @visibility-change="handleHyperlinkPopoverVisibilityChange"
        />
        <InformationPopover
          :workbook-id="workbookId"
          :is-opened="isInformationPopoverOpened"
          :is-readonly="false"
          @show-hyperlink-popover="handleInformationHyperlinkRequest"
          @show-note-popover="handleNotePopoverVisibilityChange"
          @visibility-change="handleInformationPopoverVisibilityChange"
          @close-popover="handleClosePopover"
          @context-menu="handleInformationContextMenu"
          @open-href="handleOpenHref"
        />
      </template>
      <template #right>
        <div class="tw-section-container bg-fill-surfaceDesktop-light dark:bg-fill-surfaceDesktop-dark h-full w-[264px] overflow-hidden">
          <embed-right-panel />
        </div>
      </template>
    </section-slider>

    <maker-bottom-bar v-if="embedEditorPreset.bottomBarVariant === 'simplified'" />
    <bottom-bar
      v-else
      :instance-id="Symbol('embed-editor-bottom-bar')"
      :sheets="sheets"
      :active-sheet-id="activeSheetId"
      :zoom-value="zoomValue"
      :zoom-options="zoomOptions"
      :is-editable="isEditable"
      :show-add-sheet-btn-icon="isEditable"
      :hide-sheet-btn-on-mobile="hideSheetBtnOnMobile"
      @fit-map="fitMap"
      @zoom-change="updateZoomScale"
      @switch-sheet="switchSheet"
      @new-sheet="newSheet"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue'
import { storeToRefs } from 'pinia'
import SectionSlider from 'client/components/base/section-slider.vue'
import BottomBar from 'app/fragments/shared/editor-bottom-bar/bottom-bar.vue'
import PanelTab from 'app/fragments/shared/editor-top-bar/panel-tab.vue'
import EmbedTopBar from './components/editor-top-bar/top-bar.vue'
import EmbedRightPanel from './components/editor-right-panel/right-panel.vue'
import MakerBottomBar from './components/editor-bottom-bar/maker-bottom-bar.vue'
import Editor from './editor.vue'
import EmbedEditorContextMenu from './editor-context-menu/embed-editor-context-menu.vue'
import { documentKit } from 'compositions/mindmap'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { useEditorPanelManage } from 'app/composables/use-editor-panel-manage'
import { useSheetBar } from 'client/pages/app/fragments/shared/editor-bottom-bar/sheet-bar'
import { useZoomControl } from 'client/pages/app/fragments/shared/editor-bottom-bar/zoom-control'
import { useWindowResize } from 'client/composables/window-resize'
import { useFrameStore } from 'app/services/pinia/frame-store'
import { useServiceStore } from 'client/store'
import { useGlobalConfig } from '../composables/use-global-config'
import type { EditorRightPanelTab } from 'app/types/editor-right-panel'
import type { IconName } from 'client/components/icon/icons'
import type { EditorNextFile } from 'client/types/file'
import { usePresetConfig } from 'client/pages/integration-embed-editor/composables/use-preset-config'
import { useEditorFeaturePaywall } from 'app/composables/editor-feature-paywall'

const LabelEditor = defineAsyncComponent(() => import('client/components/popovers/label.vue'))
const NotesEditor = defineAsyncComponent(() => import('client/components/popovers/notes.vue'))
const InformationPopover = defineAsyncComponent(() => import('client/components/popovers/information.vue'))
const HyperlinkEditor = defineAsyncComponent(() => import('client/components/dialogs/editor/hyperlink/index.vue'))

const props = withDefaults(
  defineProps<{
    cacheId: string
    file: EditorNextFile | ArrayBuffer
    fileName?: string
    newDoc?: documentKit.Document | null
  }>(),
  {
    fileName: 'Untitled.xmind',
    newDoc: null,
  },
)

const workspaceStore = useWorkspaceStore()
const { editor: editorState, mapName } = storeToRefs(workspaceStore)
const { sheets, activeSheetId, switchSheet } = useSheetBar()
const { zoomValue, zoomOptions, updateZoomScale, fitMap } = useZoomControl()
const { isPhoneSizeViewportWidth } = useWindowResize()
const frameStore = useFrameStore()
frameStore.setToolbarTextVisible(true)
const serviceStore = useServiceStore()
const { embedEditorPreset } = storeToRefs(useGlobalConfig())
const contextMenuRef = ref<InstanceType<typeof EmbedEditorContextMenu> | null>(null)
const { isLabelPopoverOpened, isNotesPopoverOpened, isInformationPopoverOpened, isHyperlinkPopoverOpened } = storeToRefs(frameStore)
const workbookId = computed(() => editorState.value?.cacheId)
const presetConfig = usePresetConfig()
const currentScene = presetConfig.currentScene

const isEditorLoaded = computed(() => editorState.value?.mapStatus === 'loaded')
const mapTitle = computed(() => mapName.value || props.fileName)
const isMapEditable = computed(() => isEditorLoaded.value)
const isToolbarVisible = computed(() => isMapEditable.value)

const isEditable = computed(() => currentScene === 'maker')
// hide sheet btn in 'maker' mode on mobile
const hideSheetBtnOnMobile = computed(() => {
  return currentScene === 'maker' && isPhoneSizeViewportWidth.value
})

const { currentRightPanelTab, openRightPanel, closeRightPanel } = useEditorPanelManage()
const rightPanelTabs = computed((): { name: EditorRightPanelTab; icon: IconName; label: string; isToggleAble?: boolean }[] => [
  { name: 'MARKER', icon: 'marker', label: 'Marker', isToggleAble: true },
  { name: 'STYLE', icon: 'panel-right', label: 'Format', isToggleAble: true },
])

const rightPanelTabModel = computed<EditorRightPanelTab | ''>({
  get: () => currentRightPanelTab.value ?? '',
  set: value => {
    if (!value) {
      closeRightPanel()
    } else {
      openRightPanel(value)
    }
  },
})

const isFormatPanelDisabled = computed(() => !isMapEditable.value)
const isShowRightPanel = computed(() => embedEditorPreset.value.showRightPanel && currentRightPanelTab.value && !isFormatPanelDisabled.value)
const shouldShowPanelTab = computed(() => embedEditorPreset.value.showRightPanel)

const handleLabelPopoverVisibilityChange = (isVisible: boolean) => {
  serviceStore.services?.action?.ui?.openLabelPopover?.execute({ status: isVisible })
}

const handleNotePopoverVisibilityChange = (isVisible: boolean) => {
  serviceStore.services?.action?.ui?.openNotePopover?.execute({ status: isVisible })
}

const handleHyperlinkPopoverVisibilityChange = (isVisible: boolean) => {
  serviceStore.services?.action?.ui?.openHyperlinkPopover?.execute({ status: isVisible })
}

const handleInformationPopoverVisibilityChange = (isVisible: boolean) => {
  serviceStore.services?.action?.ui?.openInformationPopover?.execute({ status: isVisible })
}

const handleInformationHyperlinkRequest = () => {
  handleHyperlinkPopoverVisibilityChange(true)
}

const handleInformationContextMenu = ({ event, type }: { event: MouseEvent; type: 'note' | 'hyperlink' }) => {
  if (type === 'note') {
    contextMenuRef.value?.showNoteContextMenu({ x: event.clientX, y: event.clientY })
  }
}

const handleClosePopover = () => {
  frameStore.setActivatedPopover(null)
}

const handleOpenHref = (payload: { href: string; sourceTopicId?: string; attachmentFilename?: string }) => {
  serviceStore.services?.action?.common?.openHref?.execute(payload)
}

const newSheet = async () => {
  if (currentScene === 'maker') {
    useEditorFeaturePaywall().showEditorFeatureLimitPaywall({ trackingAction: 'new_sheet' })
    return
  }
  // await useActionCompose().checkTeamSheetCreationAllowed({
  //   teamId: defaultTeamId.value,
  //   currentMapSheetsCount: sheets.value.length,
  //   onAllowed: async () => {
  //     useSheetBar().newSheet()
  //   },
  // })
}
</script>
